import API from "@/api";
import * as utils from "@/utils";
import * as moment from "moment";

const initialSearchFilterState = () => {
  return {
    simple: "",
    serialNumber: "",
    certStatus: [],
    certPublicKeyAlgorithm: [],
    certSignatureAlgorithm: [],
    certUsageType: [],
    validFrom: "",
    validTo: "",
    subjectDn: "",
    issuerDn: ""
  };
};

const initialState = () => {
  return {
    statisticsTimestamp: null,
    expiringCertificates: [],
    certificateStatistics: [],
    searchFilter: initialSearchFilterState(),
    tableContext: {
      currentPage: 1,
      perPage: 20,
      sortBy: null,
      sortDesc: null
    },
    columnsVisible: ["subjectDn", "issuerDn", "serialNumber", "validNotBefore", "validNotAfter", "certStatus"],
    selectedCertificates: []
  };
};

// TODO: Rename this whole class/usages to 'certificateSearch'
const certificate = {
  namespaced: true,
  state: initialState(),
  getters: {
    statisticsTimestamp: state => state.statisticsTimestamp,
    expiringCertificates: state => state.expiringCertificates,
    certificateStatistics: state => state.certificateStatistics,
    /* Search page */
    searchFilter: state => state.searchFilter,
    tableContext: state => state.tableContext,
    /* Selected/visible columns */
    columnsVisible: state => state.columnsVisible,
    /* Selected certificates */
    selectedCertificates: state => state.selectedCertificates
  },
  mutations: {
    set_statisticsTimestamp(state, statisticsTimestamp) {
      state.statisticsTimestamp = statisticsTimestamp;
    },
    set_expiringCert(state, response) {
      state.expiringCertificates = response.data.certificates;
    },
    set_certificate_statistics(state, certificateStatistics) {
      state.certificateStatistics = certificateStatistics;
    },
    resetState(state) {
      Object.assign(state, initialState());
    },
    /* Search page*/
    set_searchFilter(state, searchFilter) {
      state.searchFilter = Object.assign({}, searchFilter);
      state.tableContext = Object.assign({}, state.tableContext, { currentPage: 1 }); // Reset page
      state.selectedCertificates = []; // Reset selected certificates
    },
    reset_searchFilter(state) {
      state.searchFilter = Object.assign({}, initialSearchFilterState());
      state.tableContext = Object.assign({}, state.tableContext, { currentPage: 1 }); // Reset page
      state.selectedCertificates = []; // Reset selected certificates
      state.columnsVisible = state.columnsVisible.filter(item => item != "certPublicKeyAlgorithm" && item != "certSignatureAlgorithm" && item != "certUsageType"); //Reset the default hidden columns
    },
    resetAndUpdate_searchFilter(state, searchFilter) {
      state.searchFilter = Object.assign({}, initialSearchFilterState(), searchFilter);
      state.tableContext = Object.assign({}, state.tableContext, { currentPage: 1 }); // Reset page
      state.selectedCertificates = []; // Reset selected certificates
    },
    /* Updates all fields in the table context at once */
    set_tableContext(state, tableContext) {
      // If sort changed, reset page.
      if (state.tableContext.sortBy !== tableContext.sortBy || state.tableContext.sortDesc !== tableContext.sortDesc) {
        tableContext.currentPage = 1;
      }
      state.tableContext = Object.assign({}, tableContext);
    },
    /* Updates single fields in the table context (partial update) */
    update_tableContext(state, tableContext) {
      state.tableContext = Object.assign({}, state.tableContext, tableContext);
    },
    /* Selected/visible columns */
    set_columnsVisible(state, newColumnsVisible) {
      state.columnsVisible = newColumnsVisible;
    },
    addFromGraph_columnsVisible(state, newColumnsVisible) {
      state.columnsVisible = state.columnsVisible.filter(item => item !== "certPublicKeyAlgorithm" && item !== "certSignatureAlgorithm" && item !== "certUsageType"); //Reset the default hidden columns
      state.columnsVisible = state.columnsVisible.concat(newColumnsVisible);
    },
    addFromForm_columnsVisible(state, newColumnsVisible) {
      state.columnsVisible = state.columnsVisible.concat(newColumnsVisible);
    },
    /* Selected certificates */
    set_selectedCertificates(state, selectedCertificates) {
      state.selectedCertificates = selectedCertificates;
    }
  },
  actions: {
    async createCertificate({ commit }, certificateRequest) {
      commit("startLoading", null, { root: true });
      try {
        return await API.createCertificate(
          certificateRequest.country,
          certificateRequest.commonName,
          certificateRequest.org,
          certificateRequest.orgUnit,
          certificateRequest.email,
          certificateRequest.domainName,
          certificateRequest.validFor,
          certificateRequest.certUsageType,
          certificateRequest.certCryptoType,
          certificateRequest.keyPairMode,
          certificateRequest.pkcs10ReqBase64,
          certificateRequest.certPublicKeyAlgorithm,
          certificateRequest.certSignatureAlgorithm
        );
      } finally {
        commit("set_statisticsTimestamp", null); // make the statistics timer null so tht after every certificate creation it fetches new data for chart
        commit("stopLoading", null, { root: true });
      }
    },
    async getCertificate({ commit }, { certId }) {
      commit("startLoading", null, { root: true });
      try {
        return await API.getCertificateDetail(certId);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async searchCertificates({ commit }, { pageable, searchCriteria }) {
      commit("startLoading", null, { root: true });
      try {
        return await API.searchCertificates(pageable, searchCriteria);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async getTopExpiringCertificates({ commit }) {
      const response = await API.getTopExpiringCertificates();
      commit("set_expiringCert", response);
      return response;
    },
    async getCertificateStatisticsForUser({ commit, getters }) {
      // performance fix  fetch data every 1 min interval/new certificate creation and update from the server to show in dashboard
      var timestamp = getters.statisticsTimestamp;
      var now = new Date();
      if (timestamp != null) {
        var diff = moment.duration(moment(now).diff(moment(timestamp)));
        if (diff.asMinutes() < 1) {
          return getters.certificateStatistics;
        }
      }

      commit("startLoading", null, { root: true });
      try {
        const response = await API.getCertificateStatisticsForUser();
        commit("set_statisticsTimestamp", new Date());
        commit("set_certificate_statistics", response);
        return response;
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async parseCsrBase64({ commit }, pkcs10ReqBase64) {
      commit("startLoading", null, { root: true });
      try {
        return await API.parseBase64(pkcs10ReqBase64);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async downloadCertificate({ commit }, { certId }) {
      commit("startLoading", null, { root: true });
      try {
        return await API.downloadCertificate(certId);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async revokeCertificate({ commit }, { certId }) {
      commit("startLoading", null, { root: true });
      try {
        return await API.revokeCertificate(certId);
      } finally {
        commit("set_statisticsTimestamp", null);
        commit("stopLoading", null, { root: true });
      }
    },
    async revokeMultipleCertificates({ commit }, { certIds }) {
      commit("startLoading", null, { root: true });
      try {
        return await API.revokeMultipleCertificates(certIds);
      } finally {
        commit("set_statisticsTimestamp", null);
        commit("stopLoading", null, { root: true });
      }
    },
    async downloadMultipleCertificates({ commit }, { certIds }) {
      commit("startLoading", null, { root: true });
      try {
        return await API.downloadMultipleCertificates(certIds);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async downloadCsv({ commit, getters }, { certIds }) {
      commit("startLoading", null, { root: true });
      try {
        // First 1000 results only
        const pageable = {
          page: 0,
          size: 1000
        };

        // Find the certificates by ID
        const searchCriteriaArray = certIds.map(certId => {
          return {
            key: "id",
            operation: "EQUALS",
            value: certId,
            logicalOperation: "OR"
          };
        });

        const response = await API.searchCertificates(pageable, searchCriteriaArray);

        // Build the CSV
        const headers = getters["columnsVisible"];
        const certificates = response.data.content;
        return utils.buildCsv(headers, certificates);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    /* Selected/visible columns */
    setColumnsVisible({ commit }, columnsVisible) {
      commit("set_columnsVisible", columnsVisible);
    },
    /* Selected certificates */
    setSelectedCertificates({ commit }, selectedCertificatesArray) {
      commit("set_selectedCertificates", selectedCertificatesArray);
    },
    addSelectedCertificates({ commit, getters }, toAdd) {
      const current = getters.selectedCertificates;
      const updated = Array.from(new Set([...current, ...toAdd])); // Concat and remove duplicates
      commit("set_selectedCertificates", updated);
    },
    removeSelectedCertificates({ commit, getters }, toRemove) {
      const current = getters.selectedCertificates;
      const updated = current.filter(el => !toRemove.includes(el)); // Remove existing
      commit("set_selectedCertificates", updated);
    }
  }
};

export default certificate;
